import { inject, Injectable } from "@angular/core";
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

import { LgNetworkErrorService } from "../error-tracking/lg-network-error.service";

/**
 * HTTP interceptor that catches 5xx server errors and network failures.
 * Generates tracking GUIDs and logs via ApplicationEventTracerHub.
 * Handles error storms with deduplication (5-second cooldown).
 *
 * Interceptor order: This should be placed early in the interceptor chain (close to the caller)
 * to catch errors from auth interceptors and other network infrastructure components.
 */
@Injectable()
export class LgNetworkErrorInterceptor implements HttpInterceptor {
    private _networkErrorService = inject(LgNetworkErrorService);

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if (this._shouldTrackError(error)) {
                    const errorMessage = this._buildErrorMessage(error);
                    this._networkErrorService.trackError(errorMessage, {
                        url: error.url ?? req.url,
                        status: error.status
                    });
                }

                return throwError(() => error);
            })
        );
    }

    /**
     * Determine if this error should be tracked.
     * Only track network failures (status 0), not application-level server errors (5xx).
     */
    private _shouldTrackError(error: HttpErrorResponse): boolean {
        return error.status === 0;
    }

    /**
     * Build a descriptive error message for logging
     */
    private _buildErrorMessage(error: HttpErrorResponse): string {
        return `Network error: ${error.message || "Unable to connect to server"}`;
    }
}
